"use client";

import { Lightbulb, Shield, Zap, Globe, Users, TrendingUp, ClipboardList, Cog, CircleCheck } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { useState } from "react";
export default function OurServices() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1,
    once: true
  });
  const {
    ref: workflowRef,
    isVisible: isWorkflowVisible
  } = useRevealOnScroll({
    threshold: 0.2,
    once: false
  });
  const services = [{
    id: 1,
    icon: Lightbulb,
    title: "Concept Exploration & Feasibility",
    description: "Technical feasibility assessments | Material and process considerations | Early prototype direction | Risk identification"
  }, {
    id: 2,
    icon: TrendingUp,
    title: "Materials & Process Development",
    description: "Material selection and refinement |  Process development and optimization |  Supplier engagement"
  }, {
    id: 3,
    icon: Zap,
    title: "Design Optimization & Prototyping",
    description: "Performance-driven design iteration | Rapid prototyping | Integration of testing results into design refinements"
  }, {
    id: 4,
    icon: Users,
    title: "Performance Testing & Validation",
    description: "Custom test method development | Product benchmarking | Validation plans and data analysis"
  }, {
    id: 5,
    icon: Globe,
    title: "Manufacturing Readiness",
    description: "Design-for-manufacturing (DFM) reviews | Scale-up guidance | Technology transfer to production partners"
  }, {
    id: 6,
    icon: Shield,
    title: "IP Protection",
    description: "Patentability assessments | Invention scope expansion | Patent documentation and filing"
  }];
  const workflowPhases = [{
    id: 1,
    icon: ClipboardList,
    title: "Phase-Based Development",
    description: "We structure projects into clear, manageable phases with defined milestones and deliverables. Each phase builds upon the previous, ensuring systematic progress toward your product goals while maintaining flexibility to adapt to new insights."
  }, {
    id: 2,
    icon: Cog,
    title: "Fixed-Scope Technical Projects",
    description: "For well-defined technical challenges, we offer fixed-scope engagements with clear outcomes, timelines, and budgets. This approach provides predictability and ensures focused execution on specific development objectives."
  }, {
    id: 3,
    icon: CircleCheck,
    title: "Ongoing Advisory Support",
    description: "As your product pipeline evolves, we provide continuous technical guidance and strategic support. Our advisory services help you navigate emerging challenges, evaluate new opportunities, and maintain development momentum."
  }];
  return <section ref={sectionRef} className="bg-light-background py-16 px-4 sm:px-6 lg:px-8 pb-32">
      <div className="max-w-7xl mx-auto">
        <div className={`relative text-center mb-12 py-16 px-6 rounded-xl bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1475438/img/product_development_consulting_hero_background.png)] bg-cover bg-center transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="absolute inset-0 bg-black/50 rounded-xl"></div>
          <div className="relative z-10">
            <h1 className="text-5xl font-display font-bold text-white mb-4 drop-shadow-lg">
              Build Better Products, Faster
            </h1>
            <p className="text-lg text-white max-w-2xl mx-auto drop-shadow-md">
              We partner with companies to navigate technical uncertainty, optimize performance, and bring high-quality products to market with confidence.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {services.map((service, i) => <ServiceCard key={service.id} service={service} index={i} isVisible={isVisible} data-index={i} />)}
        </div>

        <div ref={workflowRef} className={`mt-24 transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
        transitionDelay: "600ms"
      }}>
          <div className="text-center mb-12">
            <h2 className="text-4xl font-display font-bold text-foreground mb-4">
              How We Work
            </h2>
            <p className="text-lg text-muted max-w-3xl mx-auto">
              Our flexible engagement models are designed to meet you where you are, providing the right level of support for your product development journey.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {workflowPhases.map((phase, i) => <WorkflowCard key={phase.id} phase={phase} index={i} isVisible={isVisible} data-index={i} />)}
          </div>
        </div>

        {isWorkflowVisible && <div className="fixed bottom-0 left-0 right-0 z-50 flex justify-center items-center py-4 bg-background/95 backdrop-blur-sm shadow-strong border-t border-border animate-fade-in-up">
            <Link to="/ContactPage">
              <Button size="lg" className="gap-2 bg-accent text-white hover:bg-accent-hover hover:shadow-medium hover:-translate-y-0.5 active:bg-accent-active active:translate-y-0 focus-visible:ring-2 focus-visible:ring-ring transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)]">
                Discuss Your Development Needs
              </Button>
            </Link>
          </div>}
      </div>
    </section>;
}
function ServiceCard({
  service,
  index,
  isVisible
}: {
  service: any;
  index: number;
  isVisible: boolean;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const Icon = service.icon;
  const delay = index * 100;
  return <Card className={`relative overflow-hidden bg-background border border-border rounded-lg shadow-soft hover:shadow-medium hover:-translate-y-2 hover:scale-[1.02] transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] cursor-pointer p-6 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
    transitionDelay: `${delay}ms`
  }} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
      <CardContent className="p-0 flex flex-col items-center text-center space-y-4">
        <div className="w-12 h-12 rounded-full bg-primary-light flex items-center justify-center">
          <Icon className="size-6 text-primary stroke-[2px]" />
        </div>

        <h3 className={`text-xl font-display font-semibold text-foreground relative inline-block transition-all duration-[var(--duration-medium)] ${isHovered ? "after:w-full" : "after:w-0"} after:content-[''] after:absolute after:left-0 after:-bottom-1 after:h-0.5 after:bg-accent after:transition-all after:duration-[var(--duration-medium)]`}>
          {service.title}
        </h3>

        <div className={`overflow-hidden transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${isHovered ? "max-h-32 opacity-100 translate-y-0" : "max-h-0 opacity-0 translate-y-4"}`}>
          <p className="text-sm text-muted leading-relaxed">
            {service.description}
          </p>
        </div>
      </CardContent>
    </Card>;
}
function WorkflowCard({
  phase,
  index,
  isVisible
}: {
  phase: any;
  index: number;
  isVisible: boolean;
}) {
  const Icon = phase.icon;
  const delay = 700 + index * 100;
  return <Card className={`bg-background border border-border rounded-lg shadow-soft p-6 transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
    transitionDelay: `${delay}ms`
  }}>
      <CardContent className="p-0 flex flex-col space-y-4">
        <div className="w-16 h-16 rounded-full bg-secondary-light flex items-center justify-center">
          <Icon className="size-8 text-secondary stroke-[2px]" />
        </div>

        <h3 className="text-xl font-display font-semibold text-foreground">
          {phase.title}
        </h3>

        <p className="text-sm text-muted leading-relaxed">
          {phase.description}
        </p>
      </CardContent>
    </Card>;
}